/**
 * Editor de blog post — réplica 1:1 del backoffice de Skeleton (adaptado al dark theme de core-admin).
 *   - Header sticky: arrow back + título + status select + botón Ver (si hay slug) + Guardar (acento)
 *   - Form: título grande tipo Notion + slug + cover image URL + excerpto
 *   - Sistema de bloques con drag & drop (@dnd-kit): heading (H2/H3/H4), text, image, product, quote, code, divider
 *   - "+ Agregar bloque" con menu para elegir tipo
 *   - Cada bloque tiene drag handle (GripVertical), label de tipo y botón eliminar (visible en hover)
 *   - Bloque de producto: combobox real con búsqueda contra /api/v1/products
 *
 * Para crear un post nuevo, navegar con postId='new' (este componente lo detecta).
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Input, Textarea } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical,
  Heading, Type, Image as ImageIcon, Package, Quote, Code as CodeIcon, Minus,
  Search, Eye,
} from 'lucide-react'

export const Route = createFileRoute('/store/$id/blog/$postId')({
  component: BlogEditor,
})

// ---------- Types ----------

interface HeadingBlock { id: string; type: 'heading'; level: 2 | 3 | 4; text: string }
interface TextBlock { id: string; type: 'text'; html: string }
interface ImageBlock { id: string; type: 'image'; url: string; alt: string; caption?: string }
interface ProductBlockData { id: string; type: 'product'; productId: string }
interface QuoteBlockData { id: string; type: 'quote'; text: string; author?: string }
interface CodeBlockData { id: string; type: 'code'; language: string; code: string }
interface DividerBlock { id: string; type: 'divider' }

type Block =
  | HeadingBlock
  | TextBlock
  | ImageBlock
  | ProductBlockData
  | QuoteBlockData
  | CodeBlockData
  | DividerBlock

interface PostDetail {
  id: string
  slug: string
  coverImage: string | null
  status: 'draft' | 'published' | 'archived'
  publishedAt: string | null
  title: string
  excerpt: string | null
  content: Block[]
}

// ---------- Helpers ----------

function generateId(): string {
  return Math.random().toString(36).substring(2, 10)
}

const BLOCK_PALETTE: Array<{ type: Block['type']; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { type: 'heading', label: 'Encabezado', icon: Heading },
  { type: 'text', label: 'Texto', icon: Type },
  { type: 'image', label: 'Imagen', icon: ImageIcon },
  { type: 'product', label: 'Producto', icon: Package },
  { type: 'quote', label: 'Cita', icon: Quote },
  { type: 'code', label: 'Código', icon: CodeIcon },
  { type: 'divider', label: 'Separador', icon: Minus },
]

const TYPE_LABELS: Record<Block['type'], string> = {
  heading: 'Encabezado',
  text: 'Texto',
  image: 'Imagen',
  product: 'Producto',
  quote: 'Cita',
  code: 'Código',
  divider: 'Separador',
}

function createBlock(type: Block['type']): Block {
  const id = generateId()
  switch (type) {
    case 'heading': return { id, type: 'heading', level: 2, text: '' }
    case 'text': return { id, type: 'text', html: '' }
    case 'image': return { id, type: 'image', url: '', alt: '', caption: '' }
    case 'product': return { id, type: 'product', productId: '' }
    case 'quote': return { id, type: 'quote', text: '', author: '' }
    case 'code': return { id, type: 'code', language: 'typescript', code: '' }
    case 'divider': return { id, type: 'divider' }
  }
}

/**
 * Migra bloques que vengan sin id (ej: posts viejos guardados antes del cambio
 * a sortable). Asigna un id estable para que dnd-kit pueda funcionar.
 */
function ensureBlockIds(blocks: Block[] | null | undefined): Block[] {
  if (!blocks || !Array.isArray(blocks)) return []
  return blocks.map((b) => (b.id ? b : { ...b, id: generateId() }))
}

// ---------- Component ----------

function BlogEditor() {
  const { id, postId } = Route.useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { activeTenantId } = useAuth()
  const isNew = postId === 'new'

  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [coverImage, setCoverImage] = useState('')
  const [excerpt, setExcerpt] = useState('')
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft')
  const [blocks, setBlocks] = useState<Block[]>([])
  const [showPalette, setShowPalette] = useState(false)

  const { data: existing } = useQuery({
    queryKey: ['blog', postId],
    queryFn: () => tenantApi.get<{ data: PostDetail }>(`/api/v1/blog/${postId}`),
    enabled: !!activeTenantId && !isNew,
  })

  useEffect(() => {
    if (existing?.data) {
      setTitle(existing.data.title)
      setSlug(existing.data.slug)
      setCoverImage(existing.data.coverImage ?? '')
      setExcerpt(existing.data.excerpt ?? '')
      setStatus(existing.data.status)
      setBlocks(ensureBlockIds(existing.data.content))
    }
  }, [existing])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  )

  const save = useMutation({
    mutationFn: (payload: Partial<PostDetail>) =>
      isNew
        ? tenantApi.post<{ data: { id: string } }>('/api/v1/blog', payload)
        : tenantApi.patch<{ data: { id: string } }>(`/api/v1/blog/${postId}`, payload),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['blog'] })
      if (isNew) {
        void navigate({ to: '/store/$id/blog/$postId', params: { id, postId: res.data.id } })
      }
    },
  })

  function handleSave() {
    if (!title.trim()) {
      alert('El título es requerido')
      return
    }
    if (!slug.trim()) {
      alert('El slug es requerido')
      return
    }
    save.mutate({
      title,
      slug: slug.trim().toLowerCase(),
      coverImage: coverImage || null,
      excerpt: excerpt || null,
      status,
      content: blocks,
    })
  }

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setBlocks((prev) => {
        const oldIndex = prev.findIndex((b) => b.id === active.id)
        const newIndex = prev.findIndex((b) => b.id === over.id)
        if (oldIndex === -1 || newIndex === -1) return prev
        return arrayMove(prev, oldIndex, newIndex)
      })
    }
  }, [])

  const updateBlock = useCallback((index: number, updated: Block) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? updated : b)))
  }, [])

  const deleteBlock = useCallback((index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index))
  }, [])

  function addBlock(type: Block['type']) {
    setBlocks((prev) => [...prev, createBlock(type)])
    setShowPalette(false)
  }

  function autoSlug(value: string) {
    setTitle(value)
    if (isNew && !slug) {
      const auto = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '')
      setSlug(auto)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-950">
      {/* Top bar sticky */}
      <div className="sticky top-0 z-30 bg-neutral-950 border-b border-neutral-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => void navigate({ to: '/store/$id/blog', params: { id } })}
            className="p-1.5 rounded-md hover:bg-neutral-800 text-neutral-400 hover:text-white transition"
            title="Volver"
          >
            <ArrowLeft className="size-5" />
          </button>
          <h1 className="text-lg font-semibold text-white">
            {isNew ? 'Nuevo Artículo' : 'Editar Artículo'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'draft' | 'published' | 'archived')}
            className="h-9 px-3 bg-neutral-900 border border-neutral-700 rounded-md text-sm text-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-500"
          >
            <option value="draft">Borrador</option>
            <option value="published">Publicado</option>
            <option value="archived">Archivado</option>
          </select>
          {existing?.data?.slug && (
            <a
              href={`/blog/${existing.data.slug}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 h-9 text-sm text-neutral-300 hover:text-white border border-neutral-700 hover:border-neutral-500 rounded-md transition"
              title="Ver en storefront"
            >
              <Eye className="size-4" />
              Ver
            </a>
          )}
          <button
            onClick={handleSave}
            disabled={save.isPending}
            className="inline-flex items-center gap-2 px-4 h-9 bg-orange-500 hover:bg-orange-600 text-white rounded-md text-sm font-medium transition disabled:opacity-50 disabled:pointer-events-none"
          >
            <Save className="size-4" />
            {save.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Meta fields */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-6 mb-6 space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => autoSlug(e.target.value)}
            placeholder="Título del artículo"
            className="w-full text-3xl font-bold bg-transparent text-white focus:outline-none placeholder:text-neutral-700"
          />
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-xs text-neutral-500 mb-1 block">Slug</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="url-del-articulo"
                className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded text-sm text-neutral-100 focus:outline-none focus:border-orange-500"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-neutral-500 mb-1 block">Cover Image URL</label>
              <input
                type="text"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded text-sm text-neutral-100 focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-500 mb-1 block">Extracto</label>
            <textarea
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              placeholder="Breve descripción del artículo..."
              rows={2}
              className="w-full px-3 py-2 bg-neutral-950 border border-neutral-700 rounded text-sm text-neutral-100 focus:outline-none focus:border-orange-500 resize-none"
            />
          </div>
        </div>

        {/* Blocks editor with drag & drop */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            {blocks.map((block, index) => (
              <SortableBlock
                key={block.id}
                block={block}
                onChange={(updated) => updateBlock(index, updated)}
                onDelete={() => deleteBlock(index)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* Add block menu */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowPalette(!showPalette)}
            className={cn(
              'flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg w-full justify-center text-sm transition',
              showPalette
                ? 'border-orange-500 text-orange-400'
                : 'border-neutral-700 text-neutral-500 hover:border-orange-500 hover:text-orange-400',
            )}
          >
            <Plus className="size-4" />
            Agregar bloque
          </button>
          {showPalette && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowPalette(false)} />
              <div className="absolute z-20 w-full mt-2 bg-neutral-900 border border-neutral-800 rounded-lg shadow-lg p-2">
                {BLOCK_PALETTE.map(({ type, label, icon: Icon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => addBlock(type)}
                    className="flex items-center gap-3 w-full px-3 py-2 rounded hover:bg-neutral-800 text-sm text-neutral-200 transition"
                  >
                    <Icon className="size-4 text-neutral-500" />
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {save.error && (
          <div className="p-4 mt-4 rounded-lg border border-red-800 bg-red-950/20">
            <p className="text-sm text-red-400">{(save.error as Error).message}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ---------- Sortable wrapper ----------

interface SortableBlockProps {
  block: Block
  onChange: (b: Block) => void
  onDelete: () => void
}

function SortableBlock({ block, onChange, onDelete }: SortableBlockProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group relative bg-neutral-900 border border-neutral-800 rounded-lg p-4 mb-3"
    >
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 text-neutral-500 hover:text-neutral-300"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="size-4" />
        </button>
        <span className="text-xs font-medium text-neutral-500 uppercase tracking-wider">
          {TYPE_LABELS[block.type]}
        </span>
        <button
          type="button"
          onClick={onDelete}
          className="ml-auto p-1 text-neutral-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition"
          title="Eliminar bloque"
        >
          <Trash2 className="size-4" />
        </button>
      </div>

      {block.type === 'heading' && <HeadingEditor block={block} onChange={onChange} />}
      {block.type === 'text' && <TextEditor block={block} onChange={onChange} />}
      {block.type === 'image' && <ImageEditor block={block} onChange={onChange} />}
      {block.type === 'product' && <ProductEditor block={block} onChange={onChange} />}
      {block.type === 'quote' && <QuoteEditor block={block} onChange={onChange} />}
      {block.type === 'code' && <CodeEditor block={block} onChange={onChange} />}
      {block.type === 'divider' && <DividerEditor />}
    </div>
  )
}

// ---------- Per-type block editors ----------

function HeadingEditor({ block, onChange }: { block: HeadingBlock; onChange: (b: Block) => void }) {
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {([2, 3, 4] as const).map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => onChange({ ...block, level })}
            className={cn(
              'px-3 py-1 text-sm rounded transition',
              block.level === level
                ? 'bg-orange-500 text-white'
                : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700',
            )}
          >
            H{level}
          </button>
        ))}
      </div>
      <input
        type="text"
        value={block.text}
        onChange={(e) => onChange({ ...block, text: e.target.value })}
        placeholder="Texto del encabezado..."
        className={cn(
          'w-full bg-transparent border-b border-neutral-700 focus:border-orange-500 focus:outline-none py-2 text-white placeholder:text-neutral-600',
          block.level === 2 && 'text-2xl font-bold',
          block.level === 3 && 'text-xl font-semibold',
          block.level === 4 && 'text-lg font-medium',
        )}
      />
    </div>
  )
}

function TextEditor({ block, onChange }: { block: TextBlock; onChange: (b: Block) => void }) {
  return (
    <Textarea
      value={block.html}
      onChange={(e) => onChange({ ...block, html: e.target.value })}
      placeholder="Escribe el texto del párrafo (HTML permitido)..."
      rows={4}
      className="bg-neutral-950"
    />
  )
}

function ImageEditor({ block, onChange }: { block: ImageBlock; onChange: (b: Block) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-neutral-500 mb-1 block">URL de imagen</label>
        <Input
          type="url"
          value={block.url}
          onChange={(e) => onChange({ ...block, url: e.target.value })}
          placeholder="https://..."
        />
      </div>
      {block.url && (
        <div className="relative w-full max-h-48 overflow-hidden rounded bg-neutral-950 border border-neutral-800">
          <img src={block.url} alt={block.alt} className="w-full h-full object-contain" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Alt text</label>
          <Input
            type="text"
            value={block.alt}
            onChange={(e) => onChange({ ...block, alt: e.target.value })}
            placeholder="Descripción"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 mb-1 block">Caption (opcional)</label>
          <Input
            type="text"
            value={block.caption ?? ''}
            onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
            placeholder="Pie de imagen"
          />
        </div>
      </div>
    </div>
  )
}

// Product picker — combobox real contra /api/v1/products

interface ProductTranslation { locale: string; name: string; description: string | null }
interface ProductLite {
  id: string
  slug: string
  images: string[]
  translations: ProductTranslation[]
}

function productDisplayName(p: ProductLite): string {
  return p.translations[0]?.name ?? p.slug
}

function ProductEditor({ block, onChange }: { block: ProductBlockData; onChange: (b: Block) => void }) {
  const { activeTenantId } = useAuth()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(!block.productId)

  const { data, isLoading } = useQuery({
    queryKey: ['products-picker', activeTenantId],
    queryFn: () => tenantApi.get<{ data: ProductLite[] }>('/api/v1/products?limit=100'),
    enabled: !!activeTenantId,
  })

  const products = data?.data ?? []

  const selected = useMemo(
    () => products.find((p) => p.id === block.productId) ?? null,
    [products, block.productId],
  )

  const filtered = useMemo(() => {
    if (!query.trim()) return products
    const q = query.toLowerCase()
    return products.filter((p) => {
      const name = productDisplayName(p).toLowerCase()
      return name.includes(q) || p.slug.toLowerCase().includes(q)
    })
  }, [products, query])

  if (selected && !open) {
    return (
      <div className="flex items-center gap-3 p-3 border border-neutral-700 rounded bg-neutral-950">
        <div className="size-12 bg-neutral-800 rounded flex items-center justify-center overflow-hidden">
          {selected.images?.[0] ? (
            <img src={selected.images[0]} alt={productDisplayName(selected)} className="w-full h-full object-cover" />
          ) : (
            <Package className="size-5 text-neutral-500" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-white truncate">{productDisplayName(selected)}</p>
          <p className="text-xs text-neutral-500 truncate">/{selected.slug}</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-neutral-400 hover:text-white px-2 py-1 rounded hover:bg-neutral-800 transition"
        >
          Cambiar
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 border border-neutral-700 rounded px-3 py-2 bg-neutral-950">
        <Search className="size-4 text-neutral-500" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar producto..."
          className="flex-1 text-sm bg-transparent text-neutral-100 focus:outline-none placeholder:text-neutral-600"
          autoFocus
        />
        {isLoading && <span className="text-xs text-neutral-500">Cargando...</span>}
      </div>
      <div className="max-h-60 overflow-auto border border-neutral-800 rounded bg-neutral-950 divide-y divide-neutral-800">
        {filtered.length === 0 && !isLoading && (
          <p className="px-3 py-4 text-xs text-neutral-500 text-center">Sin productos</p>
        )}
        {filtered.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => {
              onChange({ ...block, productId: p.id })
              setOpen(false)
              setQuery('')
            }}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2 hover:bg-neutral-800 text-left transition',
              p.id === block.productId && 'bg-neutral-800/60',
            )}
          >
            <div className="size-8 bg-neutral-800 rounded flex items-center justify-center overflow-hidden flex-shrink-0">
              {p.images?.[0] ? (
                <img src={p.images[0]} alt={productDisplayName(p)} className="w-full h-full object-cover" />
              ) : (
                <Package className="size-3 text-neutral-500" />
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{productDisplayName(p)}</p>
              <p className="text-xs text-neutral-500 truncate">/{p.slug}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function QuoteEditor({ block, onChange }: { block: QuoteBlockData; onChange: (b: Block) => void }) {
  return (
    <div className="space-y-3 border-l-4 border-orange-500 pl-4">
      <textarea
        value={block.text}
        onChange={(e) => onChange({ ...block, text: e.target.value })}
        placeholder="Texto de la cita..."
        rows={3}
        className="w-full bg-transparent text-lg italic text-neutral-100 focus:outline-none resize-none placeholder:text-neutral-600"
      />
      <input
        type="text"
        value={block.author ?? ''}
        onChange={(e) => onChange({ ...block, author: e.target.value || undefined })}
        placeholder="— Autor (opcional)"
        className="w-full bg-transparent text-sm text-neutral-500 focus:outline-none placeholder:text-neutral-600"
      />
    </div>
  )
}

const CODE_LANGUAGES = ['javascript', 'typescript', 'python', 'html', 'css', 'json', 'bash', 'sql', 'go', 'rust', 'java', 'other']

function CodeEditor({ block, onChange }: { block: CodeBlockData; onChange: (b: Block) => void }) {
  return (
    <div className="space-y-2">
      <select
        value={block.language}
        onChange={(e) => onChange({ ...block, language: e.target.value })}
        className="px-3 py-1.5 text-sm bg-neutral-950 border border-neutral-700 text-neutral-100 rounded focus:outline-none focus:border-orange-500"
      >
        {CODE_LANGUAGES.map((lang) => (
          <option key={lang} value={lang}>{lang}</option>
        ))}
      </select>
      <textarea
        value={block.code}
        onChange={(e) => onChange({ ...block, code: e.target.value })}
        placeholder="// Escribe tu código aquí..."
        rows={6}
        className="w-full px-4 py-3 bg-neutral-950 text-green-400 font-mono text-sm rounded resize-y focus:outline-none border border-neutral-800"
        spellCheck={false}
      />
    </div>
  )
}

function DividerEditor() {
  return (
    <div className="py-2">
      <hr className="border-neutral-700" />
      <p className="text-xs text-neutral-600 text-center mt-1">Separador</p>
    </div>
  )
}
